import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress } from "@mui/material";
import { t } from "i18next";

import {
  getChatbotTitleDetails,
  getFontFamily,
  getHeaderBgColor,
  getIsMaximized,
  getIsMobileWindow,
  getMultiLanguage,
  getSpanishLanguage,
  selectFaqLoader,
  selectIsFaq,
  selectIsPhotoTourScreen,
  selectIsVideoTourScreen,
  selectLangAlert,
  selectPropertyId,
  unitSliceData,
  updateLangAlert,
  selectPerformanceReport,
  setIsPerformanceReport,
  getStandalonePage,
  getStandaloneParams,
  getIsFeedbackModalVisible,
  setIsFeedbackModalVisible,
  setStandalonePage,
  setStandaloneParams,
  setReplies,
  removeAndAddMessages,
  selectMascotSettings,
} from "slices";
import { restartChatHandle, saveHayleyChatbotFeedback, INITIAL_REPLIES } from "utils";
import { Icon, AlertModal } from "components";

import FeedBackModal from "../FeedBackModal";

import ButtonsContainer from "./ButtonsContainer";

const HeaderBar = ({ title, onBackButtonClick }) => {
  const backgroundColor = useSelector(getHeaderBgColor);
  const fontFamily = useSelector(getFontFamily);
  const titleDetails = useSelector(getChatbotTitleDetails);
  const propertyId = useSelector(selectPropertyId);
  const isFaq = useSelector(selectIsFaq);
  const changeLangAlert = useSelector(selectLangAlert);
  const isSpanishLanguage = useSelector(getSpanishLanguage);
  const isMultiLanguage = useSelector(getMultiLanguage);
  const isMaximized = useSelector(getIsMaximized);
  const isVideoTourScreen = useSelector(selectIsVideoTourScreen);
  const isPhotoTourScreen = useSelector(selectIsPhotoTourScreen);
  const { isUnitTourScreen } = useSelector(unitSliceData);
  const isMobile = useSelector(getIsMobileWindow);
  const faqLoader = useSelector(selectFaqLoader);
  const isPerformanceReport = useSelector(selectPerformanceReport);
  const isModal = useSelector(getIsFeedbackModalVisible);
  const page = useSelector(getStandalonePage);
  const params = useSelector(getStandaloneParams);
  const mascotSettings = useSelector(selectMascotSettings);

  const hasFirstMessage = mascotSettings.firstMessage?.trim().length > 0;
  const [rating, setRating] = useState(t("excellent"));
  const [comment, setComment] = useState("");
  const [isSubmit, setIsSubmit] = useState(false);
  const [feedbackLoader, setFeedbackLoader] = useState(false);
  const [showMessage, setShowMessage] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (params.type) {
      setRating(t(params.type));
    } else {
      setRating(t("excellent"));
    }
  }, [t("excellent"), params.type]);

  useEffect(() => {
    if (isFaq) {
      setShowMessage(true);

      setTimeout(() => {
        setShowMessage(false);
      }, [5000]);
    }
  }, [isFaq]);

  useEffect(() => {
    if (changeLangAlert) {
      setTimeout(() => {
        dispatch(updateLangAlert(false));
      }, [5000]);
    }
  }, [changeLangAlert]);

  const setIsModal = (value) => {
    dispatch(setIsFeedbackModalVisible(value));

    if (!value && page === "feedback") {
      resetPageAndParams();
    }
  };

  const getDefaultHayleyMessage = useCallback(() => (isMultiLanguage ? "welcomeMessage" : "Hey, I'm Hayley. How can I help you?"), [isMultiLanguage]);

  const getFeedbackType = (label) => {
    switch (label) {
      case t("complaint"):
        return "complaint";

      case t("needImprovement"):
        return "negative_feedback";

      default:
        return "positive_feedback";
    }
  };

  const resetPageAndParams = () => {
    dispatch(setStandalonePage(""));
    dispatch(setStandaloneParams({}));

    setTimeout(() => {
      dispatch(
        removeAndAddMessages([
          {
            message: hasFirstMessage ? mascotSettings.firstMessage.trim() : getDefaultHayleyMessage(),
            speaker: "bot",
            type: hasFirstMessage || !isMultiLanguage ? "translated" : null,
            createdAt: new Date().toISOString(),
            isInitMessage: true,
          },
        ])
      );

      dispatch(setReplies(INITIAL_REPLIES));
    }, 500);
  };

  const handleFeedbackSubmit = () => {
    setFeedbackLoader(true);

    let rate = rating === t("excellent") ? "thumbs_up" : "thumbs_down";
    let payload = {
      propertyId,
      message: comment,
      feedback: rate,
    };

    if (page === "feedback") {
      payload["page"] = "feedback";
      payload["feedback"] = getFeedbackType(rating);
      payload["email"] = params.email;
    }

    saveHayleyChatbotFeedback(payload)
      .then(() => {
        resetPageAndParams();
        setIsModal(false);
        setFeedbackLoader(false);
        setIsSubmit(true);

        setTimeout(() => {
          setIsSubmit(false);
        }, 5000);
      })
      .catch((e) => setIsModal(false));
  };

  const handleReportTable = async () => {
    dispatch(setIsPerformanceReport(!isPerformanceReport));
  };

  return (
    <Box className="hayley-relative">
      <Box
        data-testid="chatbot-header"
        className={`hayley-custom hayley-box-border hayley-flex hayley-w-full hayley-flex-none hayley-items-center hayley-justify-between ${
          isMaximized ? "hayley-px-6 hayley-py-4" : "hayley-px-4 hayley-py-5"
        }  hayley-font-opensans`}
        sx={{
          "&.hayley-custom": {
            backgroundColor: `${
              isMobile ? "#fff" : !(isMaximized || isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen) ? backgroundColor : null
            } !important`,
            color: isMaximized || isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen || isMobile ? "black" : "white",
            borderBottom: isMaximized || isVideoTourScreen || isPhotoTourScreen || isUnitTourScreen || isMobile ? "1px solid #DADADA" : "",
          },
        }}
      >
        {title ? (
          <div className="hayley-flex hayley-items-center hayley-gap-x-3 hover:hayley-cursor-pointer" onClick={() => onBackButtonClick()}>
            <Icon iconType="LeftArrow" classes="hayley-h-[18px] hayley-w-[18px] hayley-text-inherit" />
            <Box
              className="hayley-custom hayley-select-none hayley-text-lg hayley-font-semibold"
              sx={{ "&.hayley-custom": { fontFamily: `${fontFamily} !important` } }}
            >
              {title}
            </Box>
          </div>
        ) : (
          <Box
            data-testid="chatbot-title"
            className="hayley-custom hayley-select-none hayley-text-lg hayley-font-semibold"
            sx={{ "&.hayley-custom": { fontFamily: `${fontFamily} !important` } }}
          >
            {titleDetails?.widget_title || "Hayley"}
          </Box>
        )}

        <ButtonsContainer
          isModal={isModal}
          setIsModal={setIsModal}
          handleReportTable={handleReportTable}
          isFaq={isFaq}
          isPerformanceReport={isPerformanceReport}
        />
      </Box>
      {changeLangAlert ? (
        <AlertModal onClose={() => dispatch(updateLangAlert(false))}>
          <div>
            <span>{t("changeLangAlert1")} </span>
            <span className="hayley-font-bold">{isSpanishLanguage ? "Español" : "English"}</span>
            <span> {t("changeLangAlert2")} </span>
            <span onClick={() => restartChatHandle()} className="hayley-cursor-pointer hayley-font-bold hayley-text-custom-3 hover:hayley-text-primary">
              {t("restartChat")}
            </span>
          </div>
        </AlertModal>
      ) : null}

      {isMultiLanguage && isSpanishLanguage && isFaq && showMessage ? (
        <AlertModal icon="InfoIcon" onClose={() => setShowMessage(false)}>
          <span className="hayley-px-2">Spanish is not yet available for AMA. We'll release it soon</span>
        </AlertModal>
      ) : null}
      {isModal && (
        <FeedBackModal
          page={page}
          rating={rating}
          comment={comment}
          setRating={setRating}
          setModal={setIsModal}
          setComment={setComment}
          feedbackLoader={feedbackLoader}
          handleFeedbackSubmit={handleFeedbackSubmit}
        />
      )}
      {isSubmit && (
        <AlertModal icon="DoubleCheckIcon" onClose={() => setIsSubmit(false)} className={"hayley-font-semibold"}>
          <span data-testid="thanks_feedback">{t("thanksFeedback")}</span>
        </AlertModal>
      )}
      {faqLoader && (
        <div className=" hayley-absolute hayley-left-[50%] hayley-py-2">
          <CircularProgress
            sx={{
              color: backgroundColor,
              fontSize: "2rem",
            }}
            size={24}
          />
        </div>
      )}
    </Box>
  );
};

export default HeaderBar;
